import {
  Button,
  LoadIndicator,
  Popup,
  TextBox,
  ValidationSummary,
  Validator,
} from "devextreme-react";
import { useEffect, useRef, useState } from "react";
import { closePopup, reloadGrid } from "@/helper/DxHelper";
import { Stock, ValidationResultItem } from "@/types/types";
import { AxiosError } from "axios";
import { useStockUpdate } from "@/hooks/useStockUpdate";
import { ClickEvent } from "devextreme/ui/button";
import { RequiredRule } from "devextreme-react/validator";
import ServerResponse from "@/components/ServerResponse";
import { axiosInstance } from "@/helper/axios-instance";
import { PopupRef } from "devextreme-react/cjs/popup";
import { DataGridRef } from "devextreme-react/cjs/data-grid";
import { useAppSelector } from "@/hooks/useAppSelector";
import { TextBoxRef } from "devextreme-react/cjs/text-box";

type Props = {
  popupRef: React.RefObject<PopupRef>;
  datagridRef: React.RefObject<DataGridRef<Stock, string>>;
};

const validationGroupName = "updateStockValGroup";

export default function Update({ datagridRef, popupRef }: Props) {
  const { id, code } = useAppSelector((state) => state.stock.editStockData);
  const [serverErrors, setServerErrors] = useState<ValidationResultItem[] | null>(null);
  const { mutate, data: response, isPending, isError, error, isSuccess } = useStockUpdate();
  const [userError, setUserError] = useState<string>("");
  const [anyChange, setAnyChange] = useState(false);
  const nameRef = useRef<TextBoxRef>(null);
  const codeRef = useRef<TextBoxRef>(null);
  const salesforceIdRef = useRef<TextBoxRef>(null);

  useEffect(() => {
    if (id) {
      hydrateForm();
    }
  }, [id]);

  useEffect(() => {
    if (isSuccess) {
      if (!response?.validation.items?.length) {
        closePopup(popupRef);
      } else {
        setServerErrors(response?.validation.items);
      }
    }
  }, [isSuccess]);

  useEffect(() => {
    disableInputs(isPending);
  }, [isPending]);

  async function fetchAndSetData() {
    try {
      const response = await axiosInstance({
        method: "GET",
        url: `Stock/Get?id=${id}`,
      });
      const dataResponse = response.data.data as Stock;

      nameRef.current?.instance().option("value", dataResponse.name);
      codeRef.current?.instance().option("value", dataResponse.code);
      salesforceIdRef.current?.instance().option("value", dataResponse.salesforceID);
    } catch (error) {
      if (error instanceof AxiosError) setUserError(error.message);
    }

    setAnyChange(false);
  }

  function hydrateForm() {
    fetchAndSetData();
  }

  function disableInputs(state: boolean) {
    nameRef.current?.instance().option("disabled", state);
    codeRef.current?.instance().option("disabled", state);
    salesforceIdRef.current?.instance().option("disabled", state);
  }

  function submit(e: ClickEvent) {
    setServerErrors(null);
    setAnyChange(false);

    if (!e.validationGroup.validate().isValid || !id) return;

    const requestData: Partial<Stock> = {
      id,
      name: nameRef.current?.instance().option("value") ?? "",
      code: codeRef.current?.instance().option("value") ?? "",
      salesforceID: salesforceIdRef.current?.instance().option("value") ?? "",
    };

    mutate(requestData);
  }

  return (
    <Popup
      showCloseButton={true}
      ref={popupRef}
      title={`Update ${code}`}
      onHiding={() => {
        setServerErrors(null);
        reloadGrid(datagridRef);
      }}
    >
      <div>
        {userError ? (
          <span>{userError}</span>
        ) : (
          <form className="mx-1 grid grid-cols-1 gap-8">
            <TextBox
              label="Name*"
              ref={nameRef}
              stylingMode="outlined"
              labelMode="static"
              valueChangeEvent="keyup"
            >
              <Validator validationGroup={validationGroupName}>
                <RequiredRule message={"Name is required"} />
              </Validator>
            </TextBox>

            <TextBox
              ref={codeRef}
              label="Code*"
              labelMode="static"
              stylingMode="outlined"
              valueChangeEvent="keyup"
            >
              <Validator validationGroup={validationGroupName}>
                <RequiredRule message={"Code is required"} />
              </Validator>
            </TextBox>
            {/* <TextBox
              ref={salesforceIdRef}
              label="Salesforce ID"
              labelMode="static"
              stylingMode="outlined"
              valueChangeEvent="keyup"
            /> */}
          </form>
        )}
      </div>
      <div className="absolute bottom-4 left-4">
        <div className="text-danger">
          {isError && error instanceof AxiosError ? <span>{error.message}</span> : null}
        </div>
        <ValidationSummary validationGroup={validationGroupName} id="summary"></ValidationSummary>
        {serverErrors?.length ? <ServerResponse items={serverErrors} /> : null}
      </div>
      <div className="absolute bottom-4 right-4">
        {anyChange && (
          <Button
            text="reset"
            onClick={hydrateForm}
            useSubmitBehavior={false}
            stylingMode="text"
            className="mr-4"
          />
        )}
        <Button
          validationGroup={validationGroupName}
          useSubmitBehavior={false}
          type="default"
          height={40}
          width={isPending ? 150 : 100}
          onClick={submit}
        >
          <LoadIndicator
            id="default-btn-loading"
            className="mr-3 inline-block align-middle"
            width={25}
            height={25}
            visible={isPending}
          />
          <span className="dx-button-text">{isPending ? "Updating" : "Update"}</span>
        </Button>
      </div>
    </Popup>
  );
}
